import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

function EditClassDialog({
  open,
  onClose,
  onSave,
  classRow,
  campusName,
  weekLabel,
  teachers,
  teachersLoading,
  saving,
}) {
  const [groupName, setGroupName] = useState("");
  const [capacity, setCapacity] = useState("17");
  const [notes, setNotes] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const teacherOptions = useMemo(() => {
    return teachers.map((t) => ({
      adultId: t.adultId ?? t.id,
      label: `${t.adultName ?? ""} ${t.adultSurname ?? ""}`.trim() || `Adult ${t.adultId ?? t.id}`,
      campus: t.campus || "",
    }));
  }, [teachers]);

  useEffect(() => {
    if (open && classRow) {
      setGroupName(classRow.groupName || "");
      setCapacity(String(classRow.capacity ?? "17"));
      setNotes(classRow.notes || "");

      if (classRow.leaderAdultId) {
        const match = teacherOptions.find(
          (t) => t.adultId === classRow.leaderAdultId
        );
        setSelectedTeacher(
          match || {
            adultId: classRow.leaderAdultId,
            label: classRow.leaderName || `Adult ${classRow.leaderAdultId}`,
            campus: campusName || "",
          }
        );
      } else {
        setSelectedTeacher(null);
      }
    }
  }, [open, classRow, teacherOptions, campusName]);

  const canSave =
    groupName.trim().length > 0 && Number(capacity) > 0 && !saving;

  const handleSave = () => {
    onSave({
      groupName: groupName.trim(),
      capacity: Number(capacity) || 0,
      notes: notes.trim(),
      leaderId: selectedTeacher?.adultId ?? null,
    });
  };

  if (!classRow) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit class — {campusName} week {weekLabel}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Class name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Capacity"
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
            required
          />
          <Autocomplete
            options={teacherOptions}
            loading={teachersLoading}
            value={selectedTeacher}
            onChange={(_, value) => setSelectedTeacher(value)}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(a, b) => a?.adultId === b?.adultId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Teacher"
                placeholder="Pick a staff member"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {teachersLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditClassDialog;
